import { Injectable, signal } from '@angular/core';
import { Appointment } from '../models/appointment.model';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  private appointments = signal<Appointment[]>([]);
  private queueAutoUpdateHandle?: ReturnType<typeof setInterval>;

  constructor() {
    // Initialize with mock data
    const today = new Date();
    this.appointments.set([
      {
        id: '1',
        patientId: '1',
        patientName: 'Alice Johnson',
        doctorId: '1',
        doctorName: 'Dr. Sarah Williams',
        date: new Date(today.getTime() + 86400000),
        time: '10:00',
        duration: 30,
        type: 'Consultation',
        status: 'Scheduled',
        reason: 'Annual checkup',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '2',
        patientId: '2',
        patientName: 'Bob Smith',
        doctorId: '1',
        doctorName: 'Dr. Sarah Williams',
        date: new Date(today.getTime() + 2 * 86400000),
        time: '14:30',
        duration: 45,
        type: 'Follow-up',
        status: 'Confirmed',
        reason: 'Follow-up on diabetes management',
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ]);
  }

  private getAppointmentStart(appointment: Appointment): Date {
    const start = new Date(appointment.date);
    if (appointment.time) {
      const [hours, minutes] = appointment.time.split(':').map(Number);
      start.setHours(hours ?? 0, minutes ?? 0, 0, 0);
    }
    return start;
  }

  private updateQueueStatuses() {
    const now = new Date();
    this.appointments.update(appointments => {
      const updated = appointments.map(appointment => {
        const start = this.getAppointmentStart(appointment);
        const end = new Date(start.getTime() + (appointment.duration ?? 30) * 60000);
        let status = appointment.status;

        if (now >= end && status !== 'Completed') {
          status = 'Completed';
        } else if (now >= start && now < end && status !== 'In Progress') {
          status = 'In Progress';
        } else if (start.getTime() - now.getTime() <= 15 * 60000 && start > now && status === 'Scheduled') {
          status = 'Confirmed';
        }

        if (status !== appointment.status) {
          return { ...appointment, status, updatedAt: now };
        }
        return appointment;
      });

      return [...updated].sort((a, b) => this.getAppointmentStart(a).getTime() - this.getAppointmentStart(b).getTime());
    });
  }

  startAutoQueueUpdates() {
    if (this.queueAutoUpdateHandle) {
      return;
    }
    this.updateQueueStatuses();
    this.queueAutoUpdateHandle = setInterval(() => this.updateQueueStatuses(), 15000);
  }

  getAppointments() {
    return this.appointments.asReadonly();
  }

  getAppointmentById(id: string): Appointment | undefined {
    return this.appointments().find(a => a.id === id);
  }

  getAppointmentsByPatient(patientId: string): Appointment[] {
    return this.appointments().filter(a => a.patientId === patientId);
  }

  getAppointmentsByDoctor(doctorId: string): Appointment[] {
    return this.appointments().filter(a => a.doctorId === doctorId);
  }

  getAppointmentsByDate(date: Date): Appointment[] {
    return this.appointments().filter(a => {
      const appointmentDate = new Date(a.date);
      return appointmentDate.toDateString() === date.toDateString();
    });
  }

  addAppointment(appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Appointment {
    const newAppointment: Appointment = {
      ...appointment,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.appointments.update(appointments => [...appointments, newAppointment]);
    return newAppointment;
  }

  updateAppointment(id: string, updates: Partial<Appointment>): void {
    this.appointments.update(appointments =>
      appointments.map(a =>
        a.id === id ? { ...a, ...updates, updatedAt: new Date() } : a
      )
    );
  }

  deleteAppointment(id: string): void {
    this.appointments.update(appointments => appointments.filter(a => a.id !== id));
  }
}

